import React, { useState } from "react";
import { API_URL, axios, LOGO_URL } from "../constants";
import { ArrowLeft, AlertTriangle, CheckCircle, XCircle, RefreshCcw, Phone } from "lucide-react";
import { Link } from "react-router-dom";

export default function CancelBookingPage() {
  const [step, setStep] = useState("lookup"); // lookup | preview | confirm | done
  const [bookingId, setBookingId] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewData, setPreviewData] = useState(null);

  const handleLookup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/bookings/cancel-preview`, {
        booking_id: bookingId.trim(),
        phone: phone.trim(),
      });
      setPreviewData(res.data);
      setStep("preview");
    } catch (err) {
      setError(err.response?.data?.detail || "Booking not found. Please check your Booking ID and phone number.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/bookings/cancel-confirm`, {
        booking_id: bookingId.trim(),
        phone: phone.trim(),
        reason: reason.trim(),
      });
      setStep("done");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to cancel. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFAF5] to-[#FFF0E8]">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-[#E83E8C]/10 px-4 py-3">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft size={18} className="text-[#475569]" />
            <img src={LOGO_URL} alt="DoozyGo" className="h-8" />
          </Link>
          <a href="https://wa.me/919930001848" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#E83E8C]">
            <Phone size={14} /> Need Help?
          </a>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        {/* Step 1: Lookup */}
        {step === "lookup" && (
          <div className="bg-white rounded-2xl shadow-lg border border-[#f0e6d9] p-6 sm:p-8" data-testid="cancel-lookup">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle size={28} className="text-orange-500" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#1E1B4B]">Cancel Booking</h1>
              <p className="text-sm text-[#475569] mt-1">Enter your booking details to proceed</p>
            </div>

            <form onSubmit={handleLookup} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1E1B4B] mb-1">Booking ID</label>
                <input
                  type="text"
                  value={bookingId}
                  onChange={(e) => setBookingId(e.target.value)}
                  placeholder="e.g. DZ-260415-001"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#e5ddd3] bg-[#FFFAF5] focus:ring-2 focus:ring-[#E83E8C]/30 focus:border-[#E83E8C] text-sm"
                  required
                  data-testid="cancel-booking-id"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1E1B4B] mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone used during booking"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#e5ddd3] bg-[#FFFAF5] focus:ring-2 focus:ring-[#E83E8C]/30 focus:border-[#E83E8C] text-sm"
                  required
                  data-testid="cancel-phone"
                />
              </div>
              {error && <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg" data-testid="cancel-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-colors disabled:opacity-50"
                data-testid="cancel-lookup-btn"
              >
                {loading ? "Looking up..." : "Find My Booking"}
              </button>
            </form>

            <div className="mt-6 bg-[#FFFAF5] rounded-xl p-4 border border-[#f0e6d9]">
              <h3 className="text-xs font-semibold text-[#1E1B4B] uppercase tracking-wider mb-2">Cancellation Policy</h3>
              <ul className="space-y-1.5 text-xs text-[#475569]">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 shrink-0"></span>7+ days before event: Full refund (minus 5% processing fee)</li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-yellow-500 mt-1.5 shrink-0"></span>3-7 days before event: 50% refund</li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0"></span>Less than 3 days: No refund (amount adjustable for rescheduling)</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === "preview" && previewData && (
          <div className="bg-white rounded-2xl shadow-lg border border-[#f0e6d9] p-6 sm:p-8" data-testid="cancel-preview">
            <div className="text-center mb-5">
              <h2 className="text-xl font-bold text-[#1E1B4B]">Review Cancellation</h2>
              <p className="text-sm text-[#475569] mt-1">Please review before confirming</p>
            </div>

            {/* Booking Details */}
            <div className="bg-[#FFFAF5] rounded-xl p-4 border border-[#f0e6d9] mb-4">
              <h3 className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2">Booking Details</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-[#475569]">Booking ID</div>
                <div className="font-medium text-[#1E1B4B]">{previewData.booking_id}</div>
                <div className="text-[#475569]">Name</div>
                <div className="font-medium text-[#1E1B4B]">{previewData.customer_name}</div>
                <div className="text-[#475569]">Date</div>
                <div className="font-medium text-[#1E1B4B]">{new Date(previewData.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                <div className="text-[#475569]">Package</div>
                <div className="font-medium text-[#E83E8C]">{previewData.package}</div>
                <div className="text-[#475569]">Payment</div>
                <div className="font-medium text-[#1E1B4B]">{previewData.payment_type}</div>
              </div>
            </div>

            {/* Refund Calculation */}
            <div className={`rounded-xl p-4 border mb-4 ${previewData.refund.refund_amount > 0 ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-2 text-[#475569]">Refund Calculation</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#475569]">Amount Paid</span>
                  <span className="font-medium">Rs {previewData.refund.amount_paid?.toLocaleString()}</span>
                </div>
                {previewData.refund.refund_amount > 0 ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-[#475569]">Refund ({previewData.refund.refund_percent}%)</span>
                      <span className="font-bold text-green-600">Rs {previewData.refund.refund_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#475569]">Processing Fee</span>
                      <span className="text-[#475569]">Rs {previewData.refund.processing_fee?.toLocaleString()}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-[#475569]">Refund</span>
                    <span className="font-bold text-red-500">Rs 0 (No refund)</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2">
                  <p className="text-xs text-[#475569]">{previewData.refund.policy_applied}</p>
                  {previewData.refund.refund_amount > 0 && (
                    <p className="text-xs text-green-600 mt-1">Refund will be processed within 5-7 business days.</p>
                  )}
                  {previewData.refund.refund_amount === 0 && (
                    <p className="text-xs text-orange-600 mt-1">Amount can be adjusted for rescheduling. Call us for details.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Reason */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-[#1E1B4B] mb-1">Reason for Cancellation (optional)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Help us improve — why are you cancelling?"
                className="w-full px-4 py-2.5 rounded-xl border border-[#e5ddd3] bg-[#FFFAF5] focus:ring-2 focus:ring-[#E83E8C]/30 focus:border-[#E83E8C] text-sm resize-none"
                rows={3}
                data-testid="cancel-reason"
              />
            </div>

            {error && <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg mb-4">{error}</p>}

            <div className="flex gap-3">
              <button
                onClick={() => { setStep("lookup"); setError(""); }}
                className="flex-1 py-3 rounded-xl border border-[#e5ddd3] text-[#475569] font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                data-testid="cancel-back-btn"
              >
                <ArrowLeft size={16} /> Go Back
              </button>
              <button
                onClick={() => setStep("confirm")}
                className="flex-1 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold transition-colors"
                data-testid="cancel-proceed-btn"
              >
                Cancel Booking
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Final Confirm */}
        {step === "confirm" && (
          <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-6 sm:p-8 text-center" data-testid="cancel-final-confirm">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} className="text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-[#1E1B4B] mb-2">Are you sure?</h2>
            <p className="text-sm text-[#475569] mb-6">This action cannot be undone. Your booking will be permanently cancelled.</p>

            {error && <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg mb-4">{error}</p>}

            <div className="flex gap-3">
              <button
                onClick={() => { setStep("preview"); setError(""); }}
                className="flex-1 py-3 rounded-xl border border-[#e5ddd3] text-[#475569] font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCcw size={16} /> Keep Booking
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={loading}
                className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors disabled:opacity-50"
                data-testid="cancel-confirm-btn"
              >
                {loading ? "Cancelling..." : "Yes, Cancel"}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Done */}
        {step === "done" && (
          <div className="bg-white rounded-2xl shadow-lg border border-green-200 p-6 sm:p-8 text-center" data-testid="cancel-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-[#1E1B4B] mb-2">Booking Cancelled</h2>
            <p className="text-sm text-[#475569] mb-1">
              Booking <strong>{bookingId}</strong> has been cancelled.
            </p>
            {previewData?.refund?.refund_amount > 0 && (
              <p className="text-sm text-green-600 font-medium mb-4">
                Refund of Rs {previewData.refund.refund_amount.toLocaleString()} will be processed within 5-7 business days.
              </p>
            )}
            <p className="text-xs text-[#475569] mb-6">A confirmation email and WhatsApp message has been sent.</p>

            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E83E8C] hover:bg-[#d1356f] text-white font-semibold transition-colors"
              data-testid="cancel-go-home"
            >
              Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
